import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { formatCalendarDate } from '../../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import type { ScoutBrief, ScoutBriefItem } from '../../types/scout';

/**
 * Scout's list on the Meetings page: what this rep should chase today —
 * follow-ups due and overdue, and their customers gone quiet (api#1079).
 *
 * GET /scout/brief, which the API computes ON REQUEST in the tenant's zone, so
 * "today", "overdue" and "90 days" are the tenant's days and a follow-up marked
 * done a minute ago is already gone. Nothing here does date maths: every day
 * and every count on a row is a field of the item's `evidence`, shown as it
 * arrived, and a field that did not arrive reads `—`, never 0.
 *
 * THREE ANSWERS THAT ARE NOT "NOTHING TO CHASE":
 *   · mode `off` — Scout is not running for this company; an empty list would
 *     claim the rep is caught up.
 *   · `heldBack` > 0 — the API computed items for this rep and is not showing
 *     them yet (shadow: reviewers see them, reps do not). The brief says how
 *     many rather than pretending there are none, and so does this.
 *   · a failed or refused read — LoadFailureNotice, in place of the list.
 *
 * Each request logs a Scout run on the API side (trigger rep_brief), so the
 * query does not refetch on window focus.
 */

export const SCOUT_BRIEF_KEY = ['scout', 'brief'] as const;

/**
 * Exhaustive over ScoutNudgeType. Overdue is red (a promise already broken),
 * due today amber, gone quiet slate-blue — the relationship cooling, not a
 * deadline. Dark ink on the saturated fill, the register badge idiom.
 */
const TYPE_BADGE: Record<ScoutBriefItem['type'], { label: string; fill: string }> = {
  follow_up_overdue: { label: 'Overdue', fill: 'bg-fi-signal-red' },
  follow_up_due: { label: 'Due today', fill: 'bg-fi-signal-amber' },
  customer_quiet: { label: 'Gone quiet', fill: 'bg-fi-signal-blue' },
};
const UNKNOWN_TYPE = { label: '—', fill: 'bg-fi-signal-slate' };

/** A number exactly as the API sent it, or `—`. A string is not coerced. */
function num(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? String(v) : '—';
}

/** A YYYY-MM-DD tenant day from the evidence, as a calendar day (no zone shift), or `—`. */
function day(v: unknown): string {
  return typeof v === 'string' ? formatCalendarDate(v) ?? '—' : '—';
}

/** The evidence facts a rep would act on, per type, each labelled. */
function facts(item: ScoutBriefItem): Array<[string, string]> {
  const e = item.evidence ?? {};
  switch (item.type) {
    case 'follow_up_overdue':
      return [['Due', day(e.follow_up_date)], ['Days overdue', num(e.days_overdue)]];
    case 'follow_up_due':
      return [['Due', day(e.follow_up_date)]];
    case 'customer_quiet':
      return [['Last activity', day(e.last_activity_date)], ['Days quiet', num(e.days_quiet)]];
    default:
      return [];
  }
}

export function ScoutBriefList() {
  const query = useQuery({
    queryKey: SCOUT_BRIEF_KEY,
    queryFn: async () => unwrap<ScoutBrief>((await api.scout.brief()).data),
    refetchOnWindowFocus: false,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);

  if (failure) {
    return <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="Scout's list" className="py-4" />;
  }
  const brief = query.data;
  if (!brief) return <div className="h-16 animate-pulse rounded-lg bg-fieldiq-dark/60" aria-busy="true" />;

  if (brief.mode === 'off') {
    return (
      <p className="py-3 text-sm text-fieldiq-muted" data-testid="scout-off">
        Scout is off for your company. An admin can turn it on in Settings.
      </p>
    );
  }

  const held = typeof brief.heldBack === 'number' && brief.heldBack > 0 ? brief.heldBack : 0;
  const heldLine = held > 0 && (
    <p className="text-xs text-fieldiq-muted" data-testid="scout-held-back">
      {held} more {held === 1 ? 'item is' : 'items are'} held back while Scout is being checked.
    </p>
  );

  if (!Array.isArray(brief.items) || brief.items.length === 0) {
    return (
      <div className="py-3 space-y-1">
        <p className="text-sm text-fieldiq-muted" data-testid="scout-empty">
          {held > 0 ? 'Nothing shown yet.' : 'Nothing to chase today.'}
        </p>
        {heldLine}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="divide-y divide-fieldiq-border" aria-label="Scout's list">
        {brief.items.map((item, i) => {
          const badge = TYPE_BADGE[item.type] ?? UNKNOWN_TYPE;
          return (
            <li key={`${item.type}:${item.activityId ?? item.customerId}:${i}`} className="py-2.5" data-testid={`scout-item-${item.type}`}>
              <Link to={`/customers/${item.customerId}`} className="block hover:underline">
                <span className="flex flex-wrap items-start gap-2">
                  <span className={`inline-flex w-fit flex-shrink-0 items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.65rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${badge.fill}`}>
                    {badge.label}
                  </span>
                  <span className="min-w-0 flex-1 text-sm text-white">{item.message || '—'}</span>
                </span>
              </Link>
              {facts(item).length > 0 && (
                <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                  {facts(item).map(([k, v]) => (
                    <div key={k} className="flex gap-1" data-testid={`fact-${k}`}>
                      <dt className="text-fieldiq-muted">{k}</dt>
                      <dd className="font-semibold tabular-nums text-fieldiq-text">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          );
        })}
      </ul>
      {heldLine}
    </div>
  );
}

export default ScoutBriefList;
